import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "swiper/swiper-bundle.css";
import "simplebar-react/dist/simplebar.min.css";
import App from "./App";
import { AppWrapper } from "./components/common/PageMeta";
import { ThemeProvider } from "./context/ThemeContext";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { Analytics } from "@vercel/analytics/react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { useEffect, lazy, Suspense, useMemo, useRef } from "react";
import { GlobalErrorBoundary } from "./components/common/GlobalErrorBoundary";
import { initializeGlobalErrorHandling } from "./utils/errorHandling";
import {
  initializeAuthSecurity,
  setupActivityTracking,
  createAuth0SecurityWrapper,
} from "./utils/authSecurity";
import { initializeLazyLoading } from "./utils/imageOptimization";
import { initializeSentry } from "./utils/sentry";
import { initializeAnalytics } from "./utils/analytics";
import { isRedirecting } from "./hooks/useAuthenticatedApi";
import { PageLoadingView } from "./components/common/PageLoadingView";
import "./styles/auth-error.css";
// import { RoleProvider } from "./context/RoleContext";
import { isValidInternalRoute } from "./utils/navigation";

// Silence non-error console output in production only
if (import.meta.env.PROD) {
  try {
    // Silence non-error logs; keep warnings and errors visible in non-dev
    const methods: Array<keyof Console> = ["log", "debug", "info"];
    methods.forEach((method) => {
      console[method] = () => {};
    });
  } catch {}
}

// Lazy load auth error screens
const VerifyEmailScreen = lazy(() => import("./components/auth/VerifyEmailScreen"));
const AccessDenied = lazy(() => import("./pages/AccessDenied"));

// Smart authentication wrapper that handles errors properly
export const SmartProtectedApp = () => {
  const { isLoading, isAuthenticated, error, loginWithRedirect } = useAuth0();
  const hasRedirectedRef = useRef(false);

  // Show loading while Auth0 is initializing
  if (isLoading) {
    return <PageLoadingView />;
  }

  // If there's an Auth0 error, detect specific error codes from Post-Login Action
  if (error) {
    // Type the Auth0 error object to access additional properties
    interface Auth0Error extends Error {
      error?: string;
      error_description?: string;
      code?: string;
      errorCode?: string;
    }
    const auth0Error = error as Auth0Error;

    // Log the full error object for debugging (remove in production)
    if (import.meta.env.DEV) {
      console.log('Auth0 Error Object:', error);
      console.log('Error properties:', {
        message: error.message,
        error: auth0Error.error,
        error_description: auth0Error.error_description,
        code: auth0Error.code,
        errorCode: auth0Error.errorCode,
      });
    }

    // Extract error code from Auth0 error object
    // Auth0 wraps Post-Login Action errors as 'access_denied' but includes our custom code in message/error_description
    const errorMessage = error.message || '';
    const errorDescription = auth0Error.error_description || '';
    
    // Determine the specific error code from message or error_description
    let errorCode = 'unknown';
    
    // Check both message and error_description for our custom error codes
    const combinedErrorInfo = `${errorMessage} ${errorDescription}`.toLowerCase();
    
    if (combinedErrorInfo.includes('email_not_verified') || combinedErrorInfo.includes('verify your email')) {
      errorCode = 'email_not_verified';
    } else if (combinedErrorInfo.includes('sync_failed') || combinedErrorInfo.includes('account sync')) {
      errorCode = 'sync_failed';
    } else if (combinedErrorInfo.includes('server_config_error') || combinedErrorInfo.includes('misconfiguration')) {
      errorCode = 'server_config_error';
    } else {
      // Fallback to error property
      errorCode = auth0Error.error || 'unknown';
    }

    // Handle email_not_verified error (staff only)
    if (errorCode === 'email_not_verified') {
      return (
        <Suspense fallback={<PageLoadingView />}>
          <VerifyEmailScreen />
        </Suspense>
      );
    }

    // Handle sync_failed, server_config_error, and any other auth errors
    // All critical auth failures use the same aggressive AccessDenied screen
    return (
      <Suspense fallback={<PageLoadingView />}>
        <AccessDenied />
      </Suspense>
    );
  }

  // If not authenticated and no error, redirect to login
  // Exception: Don't redirect if we're on the access-denied page
  // Also check if useAuthenticatedApi is already handling a redirect to prevent duplicates
  // Don't redirect if we're recovering from an error boundary (check sessionStorage flag)
  const isRecoveringFromError = sessionStorage.getItem("error_boundary_recovery") === "true";
  
  if (
    !isAuthenticated &&
    window.location.pathname !== "/access-denied" &&
    !isRedirecting() &&
    !hasRedirectedRef.current &&
    !isRecoveringFromError
  ) {
    // Mark that we've initiated a redirect to prevent duplicate calls
    hasRedirectedRef.current = true;
    
    // Store the current full path (including search params) before redirecting
    sessionStorage.setItem(
      "auth0_return_to",
      window.location.pathname + window.location.search
    );
    loginWithRedirect();
    return <PageLoadingView />;
  }
  
  // Clear the error recovery flag if user becomes authenticated
  if (isAuthenticated && isRecoveringFromError) {
    sessionStorage.removeItem("error_boundary_recovery");
  }
  
  // Reset redirect flag if user becomes authenticated
  if (isAuthenticated && hasRedirectedRef.current) {
    hasRedirectedRef.current = false;
  }

  // If on access-denied page and not authenticated, render the app anyway
  // (the router will handle showing the AccessDenied component)
  if (!isAuthenticated && window.location.pathname === "/access-denied") {
    return <App />;
  }

  // User is authenticated, render the app
  return <App />;
};

// Component to handle Auth0 redirection after login
const AppWithRedirectHandler = () => {
  const { isLoading, isAuthenticated, user } = useAuth0();
  const authSec = useMemo(() => createAuth0SecurityWrapper(), []);

  useEffect(() => {
    // Wire login/logout to auth security manager to avoid false watchdog warnings
    if (!isLoading) {
      if (isAuthenticated && user?.sub) {
        authSec.onLogin(user);
      } else if (!isAuthenticated) {
        authSec.onLogout();
      }
    }
  }, [isAuthenticated, isLoading, user, authSec]);

  useEffect(() => {
    // Handle redirect after successful authentication
    if (isAuthenticated && !isLoading) {
      // Check if there's a stored return URL in sessionStorage
      const returnTo = sessionStorage.getItem("auth0_return_to");
      if (returnTo && returnTo !== window.location.pathname && isValidInternalRoute(returnTo)) {
        sessionStorage.removeItem("auth0_return_to");
        try {
          window.history.replaceState({}, document.title, returnTo);
        } catch {}
        return;
      } else if (returnTo && !isValidInternalRoute(returnTo)) {
        // Clean up invalid stored value to avoid repeated checks
        sessionStorage.removeItem("auth0_return_to");
      }
      // Fallback to last route if present
      const lastRoute = sessionStorage.getItem("selfserve_last_route");
      if (
        lastRoute &&
        lastRoute !== window.location.pathname + window.location.search &&
        isValidInternalRoute(lastRoute)
      ) {
        try {
          window.history.replaceState({}, document.title, lastRoute);
        } catch {}
      } else if (lastRoute && !isValidInternalRoute(lastRoute)) {
        sessionStorage.removeItem("selfserve_last_route");
      }
    }
  }, [isAuthenticated, isLoading]);

  return <SmartProtectedApp />;
};

// Retrieve Auth0 configuration from environment variables
const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN;
const auth0ClientId = import.meta.env.VITE_AUTH0_CLIENT_ID;

if (!auth0Domain || !auth0ClientId) {
  throw new Error(
    "Auth0 domain and client ID must be configured in environment variables (VITE_AUTH0_DOMAIN, VITE_AUTH0_CLIENT_ID)."
  );
}

// Initialize Sentry error tracking first
initializeSentry();

// Initialize analytics after Sentry
initializeAnalytics();

// Initialize global error handling
initializeGlobalErrorHandling();

// Initialize auth security
initializeAuthSecurity({
  maxConcurrentSessions: 3,
  sessionTimeoutMs: 30 * 60 * 1000, // 30 minutes
  suspiciousActivityThreshold: 15, // 15 actions per minute
});

// Setup activity tracking
setupActivityTracking();

// Initialize image lazy loading
initializeLazyLoading({
  threshold: 0.1,
  rootMargin: "50px 0px",
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <GlobalErrorBoundary>
      <Auth0Provider
        domain={import.meta.env.VITE_AUTH0_DOMAIN}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
        // Store tokens only in memory to reduce XSS exposure. Refresh token rotation keeps the session alive.
        useRefreshTokens={true}
        cacheLocation="memory"
        authorizationParams={{
          redirect_uri: window.location.origin,
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: "openid profile email offline_access",
          response_type: "code",
          response_mode: "query",
        }}
      >
        <ThemeProvider>
          <AppWrapper>
            <QueryClientProvider client={queryClient}>
              <AppWithRedirectHandler />
              <Analytics />
            </QueryClientProvider>
          </AppWrapper>
        </ThemeProvider>
      </Auth0Provider>
    </GlobalErrorBoundary>
  </StrictMode>
);
